import React, { useState } from 'react';
import '../css/Profile.css';
import Card from './Card';
import FantasyTeamView from './FantasyTeamView';
import FantasyCardStats from './FantasyCardStats';
import useTeamImages from '../hooks/useTeamImages';

const FantasyTeamStatsCard = ({ stats, loading, error, title, icon, onCardClick, onCardTeamsClick, userNumber }) => {

    if (loading) {
        return (
            <div className='profile-stats-table-container' style={{ width: '100%' }}>
                <div className='profile-information-main-container'>
                    <div className='fantasyTeamView-content' style={{ minHeight: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className='loader-overlay' style={{ backgroundColor: 'rgba(255, 255, 255, 0)' }}>
                            <div className='loader'></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='profile-stats-table-container' style={{ width: '100%' }}>
                <div className='profile-information-main-container'>
                    <div className='fantasyTeamView-content error' style={{ minHeight: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <p>Unable to load {title.toLowerCase()}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!stats && !loading) {
        return (
            <div className='profile-stats-table-container' style={{ width: '100%' }}>
                <div className='profile-information-main-container'>
                    <div className='fantasyTeamView-content' style={{ minHeight: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <p>No {title.toLowerCase()} found</p>
                    </div>
                </div>
            </div>
        );
    }

    const renderContent = () => {
        switch (title) {
            case 'Most Used Card':
                return (
                    <div className='fantasy-stats-main-container'>

                        <div className='fantasy-stats-card-container'>
                            <div onClick={() => onCardTeamsClick && onCardTeamsClick(stats, userNumber)}>
                                <Card key={stats.card._id} card={stats.card} />
                            </div>
                        </div>

                        <div className='profile-stats-table-container'>
                            <table className='profile-stats-table'>
                                <thead>
                                    <tr>
                                        <th>Statistic</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Player</td>
                                        <td>{stats.player?.name || 'Unknown'}</td>
                                    </tr>
                                    <tr>
                                        <td>Team</td>
                                        <td>{stats.team?.name || 'Unknown'}</td>
                                    </tr>
                                    <tr>
                                        <td>Times Used</td>
                                        <td>{stats.usageCount || 0}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Points</td>
                                        <td>{stats.totalPoints?.toFixed(2) || 0}</td>
                                    </tr>
                                    <tr>
                                        <td>Average Points</td>
                                        <td>{stats.averagePoints?.toFixed(2) || 0}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Boost Points</td>
                                        <td>{stats.totalBoostPoints?.toFixed(2) || 0}</td>
                                    </tr>
                                    <tr>
                                        <td>Average Boost Points</td>
                                        <td>{stats.averageBoostPoints?.toFixed(2) || 0}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                    </div>
                );

            case 'Highest Points Card':
                return (
                    <div className='fantasy-stats-main-container'>

                        <div className='profile-stats-table-container'>
                            <table className='profile-stats-table'>
                                <thead>
                                    <tr>
                                        <th>Statistic</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Player</td>
                                        <td>{stats.player?.name || 'Unknown'}</td>
                                    </tr>
                                    <tr>
                                        <td>Team</td>
                                        <td>{stats.team?.name || 'Unknown'}</td>
                                    </tr>
                                    <tr>
                                        <td>Points</td>
                                        <td>{stats.points?.toFixed(2) || 0}</td>
                                    </tr>
                                    <tr>
                                        <td>Boost Points</td>
                                        <td>{stats.boostPoints?.toFixed(2) || 0}</td>
                                    </tr>
                                    <tr>
                                        <td>Event</td>
                                        <td>{stats.event?.name || 'Unknown'}</td>
                                    </tr>
                                    <tr>
                                        <td>Owner</td>
                                        <td>{stats.user?.username || 'Unknown'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className='fantasy-stats-card-container'>
                            <div onClick={() => onCardClick && onCardClick(stats.fantasyTeam.card)} style={{ cursor: 'pointer' }}>
                                <Card key={stats.card._id} card={stats.card} />
                            </div>
                        </div>

                    </div>
                );

            default:
                return null;
        }
    };

    return (
        renderContent()
    );
};

const FantasyTeamStats = ({ fantasyStats, loading, error, userNumber, userId }) => {
    const [showOverlay, setShowOverlay] = useState(false);
    const [overlayContent, setOverlayContent] = useState(null);
    const [showCardTeamsOverlay, setShowCardTeamsOverlay] = useState(false);
    const [cardTeamsData, setCardTeamsData] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const matches = fantasyStats.highestPointsCard?.fantasyTeam?.card?.matches || [];
    const images = useTeamImages(matches);

    const handleCardClick = (cardData) => {
        setOverlayContent(cardData);
        setShowOverlay(true);
    };

    const handleCardTeamsClick = (cardData, userNumber) => {
        setShowCardTeamsOverlay(true);
        // Use the fantasyTeams data that's included in the card stats
        if (cardData.fantasyTeams && cardData.fantasyTeams.length >= 0) {
            setCardTeamsData(cardData.fantasyTeams);
        } else {
            // For cards without fantasy teams data, show empty state
            setCardTeamsData([]);
        }
    };

    const closeCardTeamsOverlay = () => {
        setShowCardTeamsOverlay(false);
        setCardTeamsData(null);
        setSelectedEvent(null);
    };

    const closeSelectedEvent = () => {
        setSelectedEvent(null);
    };

    const getPointsStyle = (point) => {
        return {
            color: point < 0 ? 'var(--lightred)' : point > 0 ? 'var(--lightgreen)' : 'inherit'
        };
    };

    return (
        <>
            <div className='title-container' style={{ position: 'relative' }}>
                <div className=' inventory-box' style={{ width: '50%' }}>
                    <h1 className='title'>Best Fantasy Team</h1>
                </div>
            </div>
            <div className='fantasy-teams-container'>
                <FantasyTeamView key={fantasyStats?.highestPointsTeam?._id} parsedFantasyTeam={fantasyStats?.highestPointsTeam} />
            </div>

            <div className='title-container' style={{ position: 'relative', marginTop: '0.5vw' }}>
                <div className=' inventory-box' style={{ width: '50%' }}>
                    <h1 className='title'>Most Used Card</h1>
                </div>
            </div>
            <FantasyTeamStatsCard
                stats={fantasyStats.mostUsedCard}
                loading={loading?.mostUsedCard}
                error={error?.mostUsedCard}
                title="Most Used Card"
                icon="📊"
                onCardClick={handleCardClick}
                onCardTeamsClick={handleCardTeamsClick}
                userNumber={userNumber}
            />

            <div className='title-container' style={{ position: 'relative', marginTop: '0.5vw' }}>
                <div className=' inventory-box' style={{ width: '50%' }}>
                    <h1 className='title'>Highest Points Card</h1>
                </div>
            </div>
            <FantasyTeamStatsCard
                stats={fantasyStats.highestPointsCard}
                loading={loading?.highestPointsCard}
                error={error?.highestPointsCard}
                title="Highest Points Card"
                icon="⭐"
                onCardClick={handleCardClick}
                onCardTeamsClick={handleCardTeamsClick}
                userNumber={userNumber}
            />

            {/* Render the card stats overlay */}
            {showOverlay && (
                <FantasyCardStats
                    fantasyCard={overlayContent}
                    onClose={() => setShowOverlay(false)}
                    teamImages={images}
                />
            )}

            {/* Render the card teams overlay */}
            {showCardTeamsOverlay && (
                <div className="overlay" onClick={closeCardTeamsOverlay}>
                    <div className='card-menu-container' style={{ height: "100%" }}>
                        <h1 className='title-eventpage'>
                            All Fantasy Teams with this Card
                        </h1>
                        <button className="overlay-close" onClick={closeCardTeamsOverlay}>X</button>
                        <div className='overlay-content' onClick={(e) => e.stopPropagation()}>
                            <div className='event-desc-container'>
                                <p className='col-7'> Event </p>
                                <p className='col-3'> Points </p>
                                <p className='col-2'> Placement </p>
                            </div>

                            <div className='leaderboard-scroll-container'>
                                {cardTeamsData && cardTeamsData.length > 0 ? (
                                    cardTeamsData.map((team, index) => (
                                        <div key={index} className="event-entry-container" onClick={() => setSelectedEvent(team.event)} >
                                            <p className='col-7'> {team.event.name} </p>
                                            <p className='col-3' style={getPointsStyle(team.points)}> {team.points.toFixed(2)} </p>
                                            <p className='col-2'> {team.placement ? team.placement : "?"} </p>
                                        </div>
                                    ))
                                ) : (
                                    <div className='no-teams-container'>
                                        <p>No fantasy teams found with this card</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Render the selected event overlay */}
            {selectedEvent && (
                <div className="overlay" onClick={closeSelectedEvent}>
                    <FantasyTeamView userID={userId} event={selectedEvent} onClose={closeSelectedEvent} />
                </div>
            )}
        </>
    );
};

export default FantasyTeamStats;
